// ──────────────────────────────────────────────────────────────
// Shared AI Script Writer modal — mount it anywhere a client types
// broadcast copy: Avatar Studio, News Center, Commercial Creator,
// DJ Assistant, Vision Project, Music News, etc.
//
// Contract:
//   <AiWriteScriptModal
//     open={boolean}
//     onClose={() => void}
//     onApply={({title, script, meta}) => void}
//     defaultScriptType="news" | "commercial" | "show" | …   (optional)
//     defaultTone="authoritative" | "energetic" | …          (optional)
//     defaultLengthSec={30}                                   (optional)
//     hideTitle={false}   // hide the "Suggested title" preview line
//     surface="commercial"  // sent to backend for lightweight analytics
//   />
//
// Backend: POST /api/vision/ai-write-script (Claude Sonnet 4.5).
// ──────────────────────────────────────────────────────────────
import React, { useEffect, useState } from "react";
import { Sparkles, X, Loader2, RefreshCw, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export const SCRIPT_TYPES = [
  { id: "news",       label: "News anchor read" },
  { id: "promo",      label: "Station promo" },
  { id: "commercial", label: "Commercial spot" },
  { id: "sweeper",    label: "Sweeper / liner" },
  { id: "weather",    label: "Weather forecast" },
  { id: "traffic",    label: "Traffic report" },
  { id: "show",       label: "Show / segment intro" },
  { id: "interview",  label: "Interview intro" },
  { id: "custom",     label: "Custom copy" },
];

export const TONE_OPTIONS = [
  { id: "authoritative", label: "Authoritative" },
  { id: "energetic",     label: "Energetic" },
  { id: "conversational",label: "Conversational" },
  { id: "calm",          label: "Calm" },
  { id: "playful",       label: "Playful" },
  { id: "breaking",      label: "Breaking news" },
];

export const LENGTH_OPTIONS = [
  { seconds: 15, label: "15s (bumper)" },
  { seconds: 30, label: "30s (short read)" },
  { seconds: 60, label: "60s (standard)" },
  { seconds: 90, label: "90s (long-form)" },
  { seconds: 120, label: "2min (in-depth)" },
];

export default function AiWriteScriptModal({
  open,
  onClose,
  onApply,
  defaultScriptType = "news",
  defaultTone = "authoritative",
  defaultLengthSec = 30,
  hideTitle = false,
  initialTopic = "",
}) {
  const [topic, setTopic] = useState(initialTopic || "");
  const [scriptType, setScriptType] = useState(defaultScriptType);
  const [tone, setTone] = useState(defaultTone);
  const [lengthSec, setLengthSec] = useState(defaultLengthSec);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);

  // Reset local state when the modal closes so it feels fresh each time.
  useEffect(() => {
    if (!open) {
      const t = setTimeout(() => {
        setTopic(initialTopic || "");
        setScriptType(defaultScriptType);
        setTone(defaultTone);
        setLengthSec(defaultLengthSec);
        setPreview(null);
        setBusy(false);
      }, 200);
      return () => clearTimeout(t);
    } else {
      // Refresh initialTopic on open (useful for pages that pre-fill from
      // brief data — e.g. commercial creator dropping product/audience in)
      setTopic(initialTopic || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const submit = async () => {
    if (!topic.trim() || topic.trim().length < 3) {
      toast.error("Give the AI a topic — even one sentence helps.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/vision/ai-write-script", {
        topic: topic.trim(),
        script_type: scriptType,
        tone,
        length_seconds: lengthSec,
        language: "en",
      });
      setPreview(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI writer failed. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const applyToScript = () => {
    if (!preview) return;
    onApply({ title: preview.title, script: preview.script, meta: preview.meta });
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="ai-write-modal"
    >
      <div
        className="w-full max-w-2xl rounded-lg border border-line bg-bg-elevated shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-line">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-brand-amber" />
            <div className="font-mono uppercase tracking-wider text-sm text-white">AI Script Writer</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-ink-muted hover:text-white transition"
            data-testid="ai-write-modal-close"
            aria-label="Close AI Script Writer"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="text-[11px] font-mono uppercase tracking-wider text-ink-muted">
              What&rsquo;s this about?
            </label>
            <textarea
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              rows={3}
              maxLength={1200}
              disabled={busy}
              placeholder="e.g. Local council approved a downtown block-party for July 20th, main streets close 4-11pm, three headliners performing free…"
              data-testid="ai-write-topic"
              className="w-full mt-1 rounded-md bg-bg border border-line px-3 py-2 text-sm focus:border-brand-amber/60 focus:outline-none font-mono disabled:opacity-50"
            />
            <div className="text-[10px] text-ink-muted mt-1">
              Drop in bullet points, article snippets, or a rough idea — the more detail, the sharper the script. Missing facts stay as bracketed placeholders.
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="text-[11px] font-mono uppercase tracking-wider text-ink-muted">Type</label>
              <select
                value={scriptType}
                onChange={(e) => setScriptType(e.target.value)}
                disabled={busy}
                data-testid="ai-write-type"
                className="w-full mt-1 rounded-md bg-bg border border-line px-2 py-2 text-sm focus:border-brand-amber/60 focus:outline-none disabled:opacity-50"
              >
                {SCRIPT_TYPES.map((t) => (
                  <option key={t.id} value={t.id}>{t.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[11px] font-mono uppercase tracking-wider text-ink-muted">Tone</label>
              <select
                value={tone}
                onChange={(e) => setTone(e.target.value)}
                disabled={busy}
                data-testid="ai-write-tone"
                className="w-full mt-1 rounded-md bg-bg border border-line px-2 py-2 text-sm focus:border-brand-amber/60 focus:outline-none disabled:opacity-50"
              >
                {TONE_OPTIONS.map((t) => (
                  <option key={t.id} value={t.id}>{t.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[11px] font-mono uppercase tracking-wider text-ink-muted">Length</label>
              <select
                value={lengthSec}
                onChange={(e) => setLengthSec(parseInt(e.target.value, 10))}
                disabled={busy}
                data-testid="ai-write-length"
                className="w-full mt-1 rounded-md bg-bg border border-line px-2 py-2 text-sm focus:border-brand-amber/60 focus:outline-none disabled:opacity-50"
              >
                {LENGTH_OPTIONS.map((l) => (
                  <option key={l.seconds} value={l.seconds}>{l.label}</option>
                ))}
              </select>
            </div>
          </div>

          {preview && (
            <div className="rounded-md border border-brand-amber/40 bg-brand-amber/5 p-4 space-y-2" data-testid="ai-write-preview">
              {!hideTitle && preview.title && (
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-wider text-ink-muted">Suggested title</div>
                  <div className="text-sm text-white font-medium">{preview.title}</div>
                </div>
              )}
              <div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-ink-muted">Draft script</div>
                <div className="text-sm text-white font-mono whitespace-pre-wrap max-h-64 overflow-y-auto pr-1 leading-relaxed">
                  {preview.script}
                </div>
              </div>
              <div className="text-[10px] text-ink-muted font-mono">
                {preview.meta?.word_count} words · ~{preview.meta?.target_seconds}s target · {preview.meta?.wpm} wpm
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-line">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-2 rounded-md text-sm text-ink-muted border border-line hover:text-white hover:border-ink-muted transition"
            data-testid="ai-write-cancel"
          >
            Cancel
          </button>
          {preview ? (
            <>
              <button
                type="button"
                onClick={submit}
                disabled={busy}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm border border-line text-white hover:border-brand-amber/60 disabled:opacity-50 transition"
                data-testid="ai-write-regenerate"
              >
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                Regenerate
              </button>
              <button
                type="button"
                onClick={applyToScript}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm bg-brand-amber text-black font-medium hover:bg-brand-amber/90 transition"
                data-testid="ai-write-apply"
              >
                <Check className="h-3.5 w-3.5" /> Use this script
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={submit}
              disabled={busy || !topic.trim()}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm bg-brand-amber text-black font-medium hover:bg-brand-amber/90 disabled:opacity-50 disabled:cursor-not-allowed transition"
              data-testid="ai-write-generate"
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
              {busy ? "Writing…" : "Generate script"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}


// ── Reusable inline trigger button ─────────────────────────────
// A styled "✨ Write for me" button matched to the app's amber accent.
// Mount next to any script textarea; wire onClick to setState(true) on
// the surface's own AiWriteScriptModal state.
export function AiWriteButton({ onClick, testid = "ai-write-script-open", label = "Write for me", size = "sm" }) {
  const sizeCls = size === "xs"
    ? "text-[10px] px-2 py-0.5 gap-1"
    : "text-[10px] px-2.5 py-1 gap-1.5";
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center ${sizeCls} font-mono uppercase tracking-wider rounded-md border border-brand-amber/50 bg-brand-amber/10 text-brand-amber hover:bg-brand-amber/20 transition`}
      title="Let AI draft the script for you — pick a topic and length"
    >
      <Sparkles className="h-3 w-3" /> {label}
    </button>
  );
}
